/**
 * Platform-admin runtime workspace browser (RuntimeOverview "Workspace"
 * tab). Read-only, lazy folder tree + file viewer for two roots on the
 * selected runtime: the org case volume (/data/org) and the OpenClaw
 * agent home (~/.clawdbot). Full unrestricted visibility — the access
 * boundary is the platform.admin scope enforced server-side.
 *
 * REST-based (NOT useGatewayRpc): goes through lexserver's
 * /api/platform/runtimes/:userId/fs/* proxy (platform.admin-gated),
 * keyed on the selected runtime's userId from useRuntimeContext.
 */

import { Bot, ChevronDown, ChevronRight, Database, File, Folder, FolderOpen } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRuntimeContext } from "@/hooks/useRuntimeContext";
import {
  listRuntimeFs,
  readRuntimeFs,
  type FsEntry,
  type FsListing,
  type FsReadResult,
  type FsRoot,
} from "@/lib/fs-browser-api";
import { formatBytes } from "@/lib/chart-helpers";
import { SourceChip } from "@/components/uploads/SourceChip";
import { UploadAttributionPanel } from "@/components/uploads/UploadAttributionPanel";
import {
  DEFAULT_UPLOAD_FILTER,
  UploadFiltersBar,
  recordPassesFilter,
  type UploadFilterState,
} from "@/components/uploads/UploadFiltersBar";
import {
  useRuntimeUploadAttribution,
  type AttributionMap,
} from "@/components/uploads/useRuntimeUploadAttribution";
import {
  resolveUploaderDisplay,
  type ExecutionLedgerRecord,
} from "@/types/execution-ledger";
import { formatRelative } from "@/components/uploads/upload-time";

const ROOTS: { id: FsRoot; label: string; hint: string; icon: LucideIcon }[] = [
  { id: "org", label: "Case workspace", hint: "/data/org", icon: Database },
  { id: "agent", label: "Agent / OpenClaw", hint: "~/.clawdbot", icon: Bot },
];

/** Empty attribution map referenced when root="agent" (the agent home
 *  dir doesn't carry upload-attribution). Stable identity so the
 *  `useMemo` on `selectedRecord` doesn't fire unnecessarily. */
const EMPTY_MAP: AttributionMap = new Map();

export function RuntimeFsBrowser() {
  const { target } = useRuntimeContext();
  // Runtimes are per-user — the browser reads the SELECTED runtime's
  // filesystem, so a runtime must be connected first (Fleet → Connect).
  const userId = target?.userId ?? null;

  const [root, setRoot] = useState<FsRoot>("org");
  const [listings, setListings] = useState<Map<string, FsListing>>(new Map());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [loadingPaths, setLoadingPaths] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<string | null>(null);
  const [file, setFile] = useState<FsReadResult | null>(null);
  const [loadingFile, setLoadingFile] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadFilter, setUploadFilter] = useState<UploadFilterState>(
    DEFAULT_UPLOAD_FILTER,
  );

  // Upload-attribution lookup — fetches every kind="upload" ledger row
  // for this runtime's userId and indexes by `${caseId}/${blobPath}`.
  // Only meaningful for root="org" (case workspace); root="agent" is
  // the OpenClaw home dir which never carries upload attribution.
  const attribution = useRuntimeUploadAttribution(userId, target?.orgId ?? null);
  const attributionMap = root === "org" ? attribution.attributionMap : EMPTY_MAP;
  const selectedRecord = useMemo<ExecutionLedgerRecord | null>(() => {
    if (!selected) return null;
    const key = attribution.resolveFsPath(selected);
    return key ? attributionMap.get(key) ?? null : null;
  }, [selected, attributionMap, attribution]);

  const loadDir = useCallback(
    async (path: string) => {
      if (!userId) return;
      setLoadingPaths((p) => new Set(p).add(path));
      try {
        const listing = await listRuntimeFs(userId, root, path);
        setListings((prev) => new Map(prev).set(path, listing));
        setError(null);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to list folder");
      } finally {
        setLoadingPaths((p) => {
          const n = new Set(p);
          n.delete(path);
          return n;
        });
      }
    },
    [userId, root],
  );

  // Reset + load the root listing whenever the root or runtime changes.
  useEffect(() => {
    setListings(new Map());
    setExpanded(new Set());
    setSelected(null);
    setFile(null);
    setError(null);
    if (userId) void loadDir("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [root, userId]);

  const toggleFolder = useCallback(
    (path: string) => {
      setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(path)) {
          next.delete(path);
        } else {
          next.add(path);
          if (!listings.has(path)) void loadDir(path);
        }
        return next;
      });
    },
    [listings, loadDir],
  );

  const openFile = useCallback(
    async (entry: FsEntry) => {
      if (!userId) return;
      setSelected(entry.path);
      setLoadingFile(true);
      setFile(null);
      try {
        const res = await readRuntimeFs(userId, root, entry.path);
        setFile(res);
        setError(null);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to read file");
      } finally {
        setLoadingFile(false);
      }
    },
    [userId, root],
  );

  if (!userId) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <Folder className="h-8 w-8 text-slate-400" />
        <h1 className="mt-2 text-xl font-semibold">Workspace Files</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Select a runtime from the fleet to browse its workspace.
        </p>
      </div>
    );
  }

  return (
    // Fill the parent's bounded height (RuntimeOverview wraps tabs in a
    // flex-1 container); the tree/viewer scroll INSIDE this, so the tree
    // never overflows below the fold (previous h-[calc(100vh-…)] cut off
    // the last rows + their scrollbar).
    <div className="flex h-full min-h-0 flex-col gap-3">
      {/* Root selector as TABS (left-aligned, active-tab underline) so it
          is unmistakable whether the Case or Agent/OpenClaw tree is shown. */}
      <div className="flex shrink-0 gap-0.5 border-b border-border" role="tablist" aria-label="Workspace root">
        {ROOTS.map((r) => {
          const Icon = r.icon;
          const active = root === r.id;
          return (
            <button
              key={r.id}
              type="button"
              role="tab"
              aria-selected={active}
              data-testid={`fs-root-${r.id}`}
              onClick={() => setRoot(r.id)}
              className={`inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-2 text-xs font-medium transition-colors ${
                active
                  ? "border-b-2 border-primary text-primary"
                  : "border-b-2 border-transparent text-muted hover:text-foreground"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {r.label}
              <span className="font-mono text-[10px] text-faint">{r.hint}</span>
            </button>
          );
        })}
      </div>

      {error && (
        <div className="shrink-0 rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400" data-testid="fs-error">
          {error}
        </div>
      )}

      {/* Upload-attribution filter bar — only meaningful under the case
          workspace root (the agent home dir never carries upload rows). */}
      {root === "org" && (
        <UploadFiltersBar
          value={uploadFilter}
          onChange={setUploadFilter}
          records={attribution.records}
          truncated={attribution.truncated}
        />
      )}

      <div className="grid min-h-0 flex-1 grid-cols-[360px_1fr] gap-4">
        {/* Tree */}
        <Card className="flex min-h-0 flex-col overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Folder className="h-4 w-4" /> {ROOTS.find((r) => r.id === root)?.hint}
            </CardTitle>
          </CardHeader>
          <CardContent className="min-h-0 flex-1 overflow-y-auto p-2" data-testid="fs-tree">
            <FsTreeLevel
              path=""
              depth={0}
              listings={listings}
              expanded={expanded}
              loadingPaths={loadingPaths}
              selected={selected}
              onToggle={toggleFolder}
              onOpenFile={openFile}
              attributionMap={attributionMap}
              resolveFsPath={attribution.resolveFsPath}
              uploadFilter={uploadFilter}
              filtersActive={root === "org"}
            />
          </CardContent>
        </Card>

        {/* Viewer */}
        <Card className="flex min-h-0 flex-col overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 truncate text-sm font-medium text-muted-foreground">
              <File className="h-4 w-4 shrink-0" />
              <span className="truncate font-mono">{selected ?? "Select a file"}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 overflow-auto p-0" data-testid="fs-viewer">
            {/* When the selected file has an upload-ledger row, show
                the attribution panel ABOVE the file content. This is
                the "drawer" surface from handoff §3 — repurposes the
                existing viewer pane rather than introducing a new
                modal/drawer component. */}
            {selectedRecord && (
              <div className="border-b border-slate-200 p-3 dark:border-slate-700">
                <UploadAttributionPanel record={selectedRecord} dense />
              </div>
            )}
            {loadingFile ? (
              <p className="p-4 text-center text-sm text-slate-400">Loading file…</p>
            ) : !file ? (
              !selectedRecord && (
                <div className="flex h-full items-center justify-center">
                  <p className="text-sm text-slate-400">Select a file to view its contents</p>
                </div>
              )
            ) : file.encoding === "base64" ? (
              <div className="space-y-2 p-4 text-sm text-slate-500 dark:text-slate-400">
                <p className="font-medium">Binary file ({formatBytes(file.size)})</p>
                <a
                  href={`data:application/octet-stream;base64,${file.content}`}
                  download={selected?.split("/").pop() ?? "file"}
                  className="inline-block rounded-md bg-indigo-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-indigo-700"
                >
                  Download
                </a>
              </div>
            ) : (
              <>
                {file.truncated && (
                  <p className="border-b border-amber-200 bg-amber-50 px-4 py-1.5 text-[11px] text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-400">
                    Truncated to {formatBytes(file.size)} — file is larger.
                  </p>
                )}
                <pre className="whitespace-pre-wrap break-words p-4 font-mono text-xs leading-relaxed">{file.content}</pre>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

interface FsTreeLevelProps {
  path: string;
  depth: number;
  listings: Map<string, FsListing>;
  expanded: Set<string>;
  loadingPaths: Set<string>;
  selected: string | null;
  onToggle: (path: string) => void;
  onOpenFile: (entry: FsEntry) => void;
  attributionMap: AttributionMap;
  /** Owned by `useRuntimeUploadAttribution` — resolves a tree path to the
   *  map's `${caseId}/${blobPath}` key via the case registry (D-21).
   *  Threaded as a prop so this component cannot re-derive an identity. */
  resolveFsPath: (fsEntryPath: string) => string | null;
  uploadFilter: UploadFilterState;
  /** When false (root="agent"), filter doesn't apply and inline
   *  attribution chips are not rendered. */
  filtersActive: boolean;
}

function FsTreeLevel(props: FsTreeLevelProps) {
  const {
    path,
    depth,
    listings,
    expanded,
    loadingPaths,
    selected,
    onToggle,
    onOpenFile,
    attributionMap,
    resolveFsPath,
    uploadFilter,
    filtersActive,
  } = props;
  const listing = listings.get(path);
  if (!listing) {
    return loadingPaths.has(path) ? (
      <p className="px-2 py-1 text-xs text-slate-400">Loading…</p>
    ) : null;
  }
  return (
    <div style={{ paddingLeft: depth === 0 ? 0 : 12 }}>
      {listing.folders.map((f) => {
        const isOpen = expanded.has(f.path);
        return (
          <div key={f.path}>
            <button
              type="button"
              data-testid="fs-folder"
              data-path={f.path}
              onClick={() => onToggle(f.path)}
              className="flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-left text-[13px] text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              {isOpen ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-slate-400" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-400" />}
              {isOpen ? <FolderOpen className="h-3.5 w-3.5 shrink-0 text-slate-400" /> : <Folder className="h-3.5 w-3.5 shrink-0 text-slate-400" />}
              <span className="truncate font-mono">{f.name}</span>
            </button>
            {isOpen && <FsTreeLevel {...props} path={f.path} depth={depth + 1} />}
          </div>
        );
      })}
      {listing.files.map((entry) => {
        // Translate the fs tree path to the canonical attribution key
        // (`${caseId}/${blobPath}`). The hook owns the join now and looks
        // the folder up in the case registry (D-21) instead of parsing an
        // identity out of the path — null means "cannot identify", never
        // a guess.
        const canonical = filtersActive
          ? resolveFsPath(entry.path)
          : null;
        const record = canonical ? attributionMap.get(canonical) : undefined;
        // Filter only applies when (a) we're under the case workspace
        // root, AND (b) the file has an attribution record. Files with
        // no attribution stay visible (the filter narrows attributed
        // files; un-attributed files are pre-attribution legacy).
        if (filtersActive && record && !recordPassesFilter(record, uploadFilter)) {
          return null;
        }
        return (
          <FsFileRow
            key={entry.path}
            entry={entry}
            selected={selected === entry.path}
            onOpen={() => onOpenFile(entry)}
            record={record}
            showAttribution={filtersActive}
          />
        );
      })}
    </div>
  );
}

function FsFileRow({
  entry,
  selected,
  onOpen,
  record,
  showAttribution,
}: {
  entry: FsEntry;
  selected: boolean;
  onOpen: () => void;
  record: ExecutionLedgerRecord | undefined;
  showAttribution: boolean;
}) {
  const uploader = record
    ? resolveUploaderDisplay(record.uploadedById, record.uploadedByEmail)
    : null;
  return (
    <button
      type="button"
      data-testid="fs-file"
      data-path={entry.path}
      onClick={onOpen}
      className={`flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-left text-[13px] transition-colors ${
        selected
          ? "bg-indigo-50 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300"
          : "text-slate-600 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-800"
      }`}
      style={{ paddingLeft: 12 + 18 }}
    >
      <File className="h-3.5 w-3.5 shrink-0 text-slate-400" />
      <span className="flex-1 truncate font-mono">{entry.name}</span>
      {/* Inline attribution chips — only rendered under the case-workspace
          root + only when the file has a matching upload-ledger row.
          Compact icon chip + truncated email + relative time. */}
      {showAttribution && record && uploader && (
        <span className="flex shrink-0 items-center gap-1 text-[10px] text-slate-400">
          <SourceChip source={record.uploadSource} compact />
          <span className="max-w-[14ch] truncate" title={uploader.label}>
            {uploader.label}
          </span>
          <span>·</span>
          <span title={record.uploadedAt}>{formatRelative(record.uploadedAt)}</span>
        </span>
      )}
      {entry.size !== undefined && (
        <span className="ml-1 shrink-0 text-[10px] text-slate-400">
          {formatBytes(entry.size)}
        </span>
      )}
    </button>
  );
}
